/**
 * Proje Yönetim Sistemi (PYS) servisi.
 *
 * Backend: gts_accounting_api → ProjectController (GET/POST-only CRUD).
 * companyId gönderilmez — JWT / X-Company-Id interceptor'ı halleder.
 * İstek gövdeleri backend DTO'larıyla uyumlu PascalCase gönderilir;
 * yanıtlar camelCase gelir ve Project arayüzüne map'lenir.
 */
import { ACCOUNTING_API } from '../config/api';
import { accountingApi } from '../config/apiClient';

export type ProjectStatus = 'active' | 'completed' | 'cancelled';

export interface Project {
  id: number;
  name: string;
  code: string | null;
  status: ProjectStatus;
  accountId: number | null;
  description: string | null;
  startDate: string | null;
  endDate: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string | null;
  /** Yalnız includeSummary=true ile listelendiğinde dolu gelir. */
  summary?: ProjectSummary | null;
}

export interface ProjectListResult {
  data: Project[];
  totalCount: number;
}

export interface CreateProjectInput {
  name: string;
  code?: string | null;
  status?: ProjectStatus;
  accountId?: number | null;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  createdBy?: string | null;
}

export interface UpdateProjectInput extends Omit<CreateProjectInput, 'createdBy'> {
  id: number;
}

const num = (value: any): number => (typeof value === 'number' ? value : Number(value ?? 0));

const mapSummary = (s: any): ProjectSummary => ({
  projectId: num(s.projectId ?? s.ProjectId),
  revenue: num(s.revenue ?? s.Revenue),
  cost: num(s.cost ?? s.Cost),
  profit: num(s.profit ?? s.Profit),
  cashIn: num(s.cashIn ?? s.CashIn),
  cashOut: num(s.cashOut ?? s.CashOut),
  netCash: num(s.netCash ?? s.NetCash),
  remainingReceivable: num(s.remainingReceivable ?? s.RemainingReceivable),
  itemCount: num(s.itemCount ?? s.ItemCount),
});

const mapProject = (p: any): Project => ({
  id: p.id ?? p.Id,
  name: p.name ?? p.Name ?? '',
  code: p.code ?? p.Code ?? null,
  status: (p.status ?? p.Status ?? 'active') as ProjectStatus,
  accountId: p.accountId ?? p.AccountId ?? null,
  description: p.description ?? p.Description ?? null,
  startDate: p.startDate ?? p.StartDate ?? null,
  endDate: p.endDate ?? p.EndDate ?? null,
  createdBy: p.createdBy ?? p.CreatedBy ?? null,
  createdAt: p.createdAt ?? p.CreatedAt ?? '',
  updatedAt: p.updatedAt ?? p.UpdatedAt ?? null,
  summary: (() => {
    const s = p.summary ?? p.Summary;
    return s ? mapSummary(s) : null;
  })(),
});

export async function getProjectsPaged(params: {
  page: number;
  pageSize: number;
  search?: string;
  status?: string;
  /** Liste kolonlarında Gelir/Maliyet/Kâr/Net Nakit gösterilecekse true. */
  includeSummary?: boolean;
}): Promise<ProjectListResult> {
  const response = await accountingApi.get(ACCOUNTING_API.PROJECT_LIST(params));
  const data = response.data;
  if (!data?.success) {
    throw new Error(data?.message || 'Projeler alınamadı.');
  }
  return {
    data: (data.data ?? []).map(mapProject),
    totalCount: data.totalCount ?? 0,
  };
}

export async function getProjectById(id: number): Promise<Project> {
  const response = await accountingApi.get(ACCOUNTING_API.PROJECT_BY_ID(id));
  const data = response.data;
  if (!data?.success || !data.data) {
    throw new Error(data?.message || 'Proje bulunamadı.');
  }
  return mapProject(data.data);
}

export async function createProject(input: CreateProjectInput): Promise<Project> {
  const response = await accountingApi.post(ACCOUNTING_API.PROJECT_CREATE, {
    Name: input.name,
    Code: input.code || null,
    Status: input.status || null,
    AccountId: input.accountId ?? null,
    Description: input.description || null,
    StartDate: input.startDate || null,
    EndDate: input.endDate || null,
    CreatedBy: input.createdBy || null,
  });
  const data = response.data;
  if (!data?.success || !data.data) {
    throw new Error(data?.message || 'Proje oluşturulamadı.');
  }
  return mapProject(data.data);
}

export async function updateProject(input: UpdateProjectInput): Promise<Project> {
  const response = await accountingApi.post(ACCOUNTING_API.PROJECT_UPDATE, {
    Id: input.id,
    Name: input.name,
    Code: input.code || null,
    Status: input.status || null,
    AccountId: input.accountId ?? null,
    Description: input.description || null,
    StartDate: input.startDate || null,
    EndDate: input.endDate || null,
  });
  const data = response.data;
  if (!data?.success || !data.data) {
    throw new Error(data?.message || 'Proje güncellenemedi.');
  }
  return mapProject(data.data);
}

/**
 * Yalnız durum geçişi (aktif ↔ tamamlandı / iptal).
 * Projeye bağlı hareketler ve özet rakamları değişmez; tamamlanan projeye
 * yeni hareket bağlanması ProjectSelector tarafından kısıtlanır.
 */
export async function updateProjectStatus(id: number, status: ProjectStatus): Promise<Project> {
  const response = await accountingApi.post(ACCOUNTING_API.PROJECT_STATUS, {
    Id: id,
    Status: status,
  });
  const data = response.data;
  if (!data?.success || !data.data) {
    throw new Error(data?.message || 'Proje durumu güncellenemedi.');
  }
  return mapProject(data.data);
}

// ============================================================================
// ÖZET + HAREKET AKIŞI (proje detay sayfası)
// ============================================================================

/** Backend ProjectEffect sabitleri. */
export type ProjectEffect = 'REVENUE' | 'COST' | 'INFO';
export type ProjectCashEffect = 'CASH_IN' | 'CASH_OUT';

/** Akış satırının kaynak tablosu (backend ProjectSourceType). */
export type ProjectSourceType =
  | 'Invoice'
  | 'Offer'
  | 'Order'
  | 'Waybill'
  | 'CashboxTransaction'
  | 'BankTransaction'
  | 'CheckNote'
  | 'AccountTransaction'
  | 'PersonnelTransaction';

export interface ProjectSummary {
  projectId: number;
  /** Faturalanan satış tutarı. */
  revenue: number;
  /** Alış faturaları + serbest giderler + personel giderleri. */
  cost: number;
  /** revenue - cost */
  profit: number;
  cashIn: number;
  cashOut: number;
  /** cashIn - cashOut */
  netCash: number;
  /** revenue - cashIn (faturalanan ama tahsil edilmeyen) */
  remainingReceivable: number;
  itemCount: number;
}

export interface ProjectFeedItem {
  entityType: ProjectSourceType | string;
  entityId: string;
  documentNo: string | null;
  date: string;
  accountName: string | null;
  description: string | null;
  /** Özet kartlarına giren TRY tutarı. */
  amount: number;
  originalAmount: number;
  currency: string | null;
  effect: ProjectEffect | string;
  cashEffect: ProjectCashEffect | string | null;
  status: string | null;
}

export interface ProjectFeedResult {
  data: ProjectFeedItem[];
  totalCount: number;
}

export async function getProjectSummary(id: number): Promise<ProjectSummary> {
  const response = await accountingApi.get(ACCOUNTING_API.PROJECT_SUMMARY(id));
  const data = response.data;
  if (!data?.success || !data.data) {
    throw new Error(data?.message || 'Proje özeti alınamadı.');
  }
  return mapSummary(data.data);
}

export async function getProjectTransactions(
  id: number,
  params: { page: number; pageSize: number },
): Promise<ProjectFeedResult> {
  const response = await accountingApi.get(ACCOUNTING_API.PROJECT_TRANSACTIONS(id, params));
  const data = response.data;
  if (!data?.success) {
    throw new Error(data?.message || 'Proje hareketleri alınamadı.');
  }
  return {
    data: (data.data ?? []).map(
      (f: any): ProjectFeedItem => ({
        entityType: f.entityType ?? f.EntityType ?? '',
        entityId: f.entityId ?? f.EntityId ?? '',
        documentNo: f.documentNo ?? f.DocumentNo ?? null,
        date: f.date ?? f.Date ?? '',
        accountName: f.accountName ?? f.AccountName ?? null,
        description: f.description ?? f.Description ?? null,
        amount: num(f.amount ?? f.Amount),
        originalAmount: num(f.originalAmount ?? f.OriginalAmount),
        currency: f.currency ?? f.Currency ?? null,
        effect: f.effect ?? f.Effect ?? 'INFO',
        cashEffect: f.cashEffect ?? f.CashEffect ?? null,
        status: f.status ?? f.Status ?? null,
      }),
    ),
    totalCount: data.totalCount ?? 0,
  };
}

export async function deleteProject(id: number): Promise<void> {
  const response = await accountingApi.post(ACCOUNTING_API.PROJECT_DELETE, { Id: id });
  const data = response.data;
  if (!data?.success) {
    throw new Error(data?.message || 'Proje silinemedi.');
  }
}
